import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { PageHeader } from '@/components/molecules';
import {
  Channels,
  Conversations,
  GrowthChart,
  Metrics,
  Performance,
} from './components';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms';
import { DatePeriodSelect } from '@/components/molecules';
import { useBusiness, useUser } from '@/api';
import { hasBusinessSettlementAccount } from '@/lib/helpers';
import { useProductTour } from '@/components/organisms';
import { routes } from '@/lib/constants';
import { getRangeFromPeriod } from '@/lib/helpers/format-date';

const KYB_MESSAGE =
  'Complete your KYB verification to unlock full access to the platform. Verification helps us confirm your business identity, enables secure transactions, and builds trust with your customers.';

const BANKING_MESSAGE =
  'You haven’t added your bank information yet. Add your bank details now so you can start receiving payments seamlessly.';

export const DashboardOverviewPage = () => {
  const [period, setPeriod] = useState<string>('30days');
  const [{ startDate, endDate }, setRange] = useState(
    getRangeFromPeriod('30days')
  );
  const [showKybModal, setShowKybModal] = useState(false);
  const [showBankingModal, setShowBankingModal] = useState(false);
  const navigate = useNavigate();
  const { data: business, isSuccess } = useBusiness();
  const { data: user } = useUser('me');
  const { isActive: isTourActive } = useProductTour();

  useEffect(() => {
    if (!isSuccess && !user) return;
    if (isTourActive) return;
    const needsKyb = !business || business.verificationStatus !== 'verified';
    setShowKybModal(needsKyb);
    setShowBankingModal(!hasBusinessSettlementAccount(business, user));
  }, [isSuccess, business, user, isTourActive]);

  return (
    <div className="flex flex-col gap-6" data-tour="dashboard-overview">
      <div className="flex justify-between items-start">
        <PageHeader
          heading="Sellyt Overview"
          description="Here is how your sales are doing today"
        />
        <DatePeriodSelect
          value={period}
          onValueChange={(val) => {
            setPeriod(val);
            setRange(getRangeFromPeriod(val));
          }}
          triggerClassName="w-fit"
        />
      </div>
      <div className="grid grid-cols-10 gap-2.5">
        <div className="col-span-10 xl:col-span-8 flex flex-col gap-2.5">
          <Metrics startDate={startDate} endDate={endDate} />
          <div className="col-span-5 grid grid-cols-5 gap-2.5">
            <Conversations />
            <div className="col-span-5 lg:col-span-2 grid grid-cols-6 gap-2.5">
              <GrowthChart startDate={startDate} endDate={endDate} />
              <Performance startDate={startDate} endDate={endDate} />
            </div>
          </div>
        </div>
        <Channels />
      </div>

      <Dialog open={showKybModal} onOpenChange={setShowKybModal}>
        <DialogContent className="sm:max-w-md" showCloseButton>
          <DialogHeader>
            <DialogTitle>Complete KYB Verification</DialogTitle>
            <DialogDescription>{KYB_MESSAGE}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:justify-end pt-4">
            <Button variant="outline" onClick={() => setShowKybModal(false)}>
              Later
            </Button>
            <Button
              onClick={() => {
                setShowKybModal(false);
                navigate(routes.kyb);
              }}
              className="bg-green-600 hover:bg-green-700"
            >
              Complete Verification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showBankingModal} onOpenChange={setShowBankingModal}>
        <DialogContent className="sm:max-w-md" showCloseButton>
          <DialogHeader>
            <DialogTitle>Complete Your Account Setup</DialogTitle>
            <DialogDescription>{BANKING_MESSAGE}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:justify-end pt-4">
            <Button
              variant="outline"
              onClick={() => setShowBankingModal(false)}
            >
              Maybe Later
            </Button>
            <Button
              onClick={() => {
                setShowBankingModal(false);
                navigate(`${routes.settings}?tab=bank-info`);
              }}
              className="bg-green-600 hover:bg-green-700"
            >
              Add Bank Details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
